/**
 * The three orthogonal "this field cannot be written" concepts (#1799).
 *
 * They answer different questions and have different backend counterparts, so
 * they must never stand in for one another:
 *
 * | concept      | question                              | backend counterpart      |
 * |--------------|---------------------------------------|--------------------------|
 * | `CanAccess`  | may THIS USER do this at all?         | 403 / 400                |
 * | `IsReadonly` | does THIS RECORD's state allow it?    | state-machine 409 / 422  |
 * | `Invisible`  | do we render it on THIS SURFACE?      | none — pure presentation |
 *
 * Substituting one for another is not a matter of style — it is a defect:
 *   - hiding a field that must not be written is fake security: the request is
 *     one DOM edit away;
 *   - expressing "a completed job cannot be edited" as a permission is wrong —
 *     a user WITH the permission still cannot edit it;
 *   - expressing "this role cannot see cost price" as domain state is wrong —
 *     that is a permission.
 *
 * The query shapes below differ deliberately so the compiler enforces the
 * separation: a permission query carries no record, a domain-state query
 * always has one, and a presentation query is per-surface and tolerates the
 * absent record of a create form.
 */

/**
 * The surfaces a field can be presented on. `as const` map → derived union, so
 * a surface name can never be a magic string at a callsite.
 */
export const FIELD_SURFACE = {
  list: 'list',
  detail: 'detail',
  form: 'form',
} as const;

export type FieldSurface = (typeof FIELD_SURFACE)[keyof typeof FIELD_SURFACE];

/** A permission question. `field` narrows it to a single column. */
export interface AccessQuery {
  resource: string;
  action: string;
  field?: string;
}

/**
 * The injected permission decision. The kit defines only the signature — the
 * shape of a permission key belongs to the consuming project, so core never
 * imports a permission catalogue, an auth context or a grant helper.
 */
export type CanAccess = (query: AccessQuery) => boolean;

/** A domain-state question about one field of one concrete record. */
export interface ReadonlyQuery<T> {
  record: T;
  field: string;
}

/** Domain state: "this record's lifecycle forbids editing this field". */
export type IsReadonly<T> = (query: ReadonlyQuery<T>) => boolean;

/**
 * A presentation question. The record is nullable because a create form has no
 * record yet, and a list column's default visibility is a table-level decision
 * with no single row behind it.
 */
export interface InvisibleQuery<T> {
  record: T | null;
  surface: FieldSurface;
  field: string;
}

/** Pure presentation: "we do not render this here". Never a security control. */
export type Invisible<T> = (query: InvisibleQuery<T>) => boolean;

/**
 * The fallback when no decision function was injected. A permission decision
 * defaults to DENY — this codebase has been burned by an `undefined` grant
 * check defaulting to allow (2026-07-17 UI feedback batch).
 */
export const DENY_ALL: CanAccess = () => false;
